import { useCallback, useEffect, useRef, useState } from "react";
import { Search, Upload, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  deleteMediaAsset,
  listMedia,
  resolveMediaUrl,
  uploadMediaFile,
} from "@/lib/media-api";

export default function MediaPickerModal({
  open,
  onClose,
  onSelect,
  kind = "image",
  title = "Workspace media",
}) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async (opts = {}) => {
    const nextPage = opts.page ?? 1;
    const query = opts.q ?? q;
    setLoading(true);
    try {
      const data = await listMedia({
        kind: kind || "",
        q: query,
        page: nextPage,
        pageSize: 48,
      });
      const rows = Array.isArray(data.items) ? data.items : [];
      setItems((prev) => (nextPage === 1 ? rows : [...prev, ...rows]));
      setHasMore(Boolean(data.hasMore));
      setPage(nextPage);
    } catch (err) {
      toast.error(err?.message || "Could not load media");
    } finally {
      setLoading(false);
    }
  }, [kind, q]);

  useEffect(() => {
    if (!open) return;
    load({ page: 1, q: "" });
    setQ("");
  }, [open, kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (ev) => {
    ev.preventDefault();
    load({ page: 1, q });
  };

  const handleUpload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const data = await uploadMediaFile(file);
        if (data?.asset) {
          setItems((prev) => [data.asset, ...prev.filter((a) => a.id !== data.asset.id)]);
        }
      }
      toast.success(files.length > 1 ? "Files uploaded" : "File uploaded");
    } catch (err) {
      toast.error(err?.message || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDelete = async (asset) => {
    if (!asset?.id) return;
    if (!window.confirm(`Remove "${asset.filename}" from the workspace library?`)) return;
    try {
      await deleteMediaAsset(asset.id);
      setItems((prev) => prev.filter((a) => a.id !== asset.id));
      toast.success("Asset removed");
    } catch (err) {
      toast.error(err?.message || "Delete failed");
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70" role="dialog" aria-modal="true">
      <div className="surface w-full max-w-4xl max-h-[90vh] flex flex-col border border-[var(--border-default)] shadow-2xl">
        <div className="flex items-center justify-between gap-3 p-4 border-b border-[var(--border-default)]">
          <div>
            <div className="text-overline">Workspace library</div>
            <h2 className="font-heading text-xl tracking-tight">{title}</h2>
          </div>
          <button type="button" className="btn-ghost p-2" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 border-b border-[var(--border-default)] flex flex-wrap gap-2 items-center">
          <form onSubmit={handleSearch} className="flex flex-1 min-w-[min(100%,240px)] gap-2 items-center">
            <div className="relative flex-1 min-w-0">
              <input
                className="input-dark input-dark--with-icon w-full"
                placeholder="Search filename…"
                value={q}
                onChange={(ev) => setQ(ev.target.value)}
                aria-label="Search media"
              />
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] pointer-events-none"
                aria-hidden
              />
            </div>
            <button type="submit" className="btn-secondary text-xs shrink-0">
              Search
            </button>
          </form>
          <button
            type="button"
            className="btn-primary text-xs"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            <Upload size={12} /> {uploading ? "Uploading…" : "Upload"}
          </button>
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            accept={kind === "document" ? ".pdf,application/pdf" : "image/*"}
            multiple
            onChange={(ev) => handleUpload(ev.target.files)}
          />
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          {loading && !items.length ? (
            <p className="text-sm text-[var(--text-secondary)]">Loading…</p>
          ) : null}
          {!loading && !items.length ? (
            <p className="text-sm text-[var(--text-secondary)]">No assets yet. Upload images or PDFs to reuse across modules.</p>
          ) : null}
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {items.map((asset) => {
              const thumb = resolveMediaUrl(asset.thumbUrl || asset.url);
              const isImage = asset.kind === "image";
              return (
                <div key={asset.id} className="group relative border border-[var(--border-default)] bg-[var(--bg-elev)]">
                  <button
                    type="button"
                    className="w-full aspect-square overflow-hidden block"
                    onClick={() => {
                      onSelect?.(asset);
                      onClose?.();
                    }}
                  >
                    {isImage ? (
                      <img src={thumb} alt={asset.filename || ""} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full grid place-items-center text-[10px] font-mono uppercase p-2 text-center text-[var(--text-tertiary)]">
                        PDF
                      </div>
                    )}
                  </button>
                  <button
                    type="button"
                    className="absolute top-1 right-1 p-1 bg-black/60 text-white opacity-0 group-hover:opacity-100 transition"
                    onClick={() => handleDelete(asset)}
                    aria-label="Delete"
                  >
                    <Trash2 size={12} />
                  </button>
                  <div className="px-1.5 py-1 text-[9px] font-mono truncate text-[var(--text-tertiary)]" title={asset.filename}>
                    {asset.filename}
                  </div>
                </div>
              );
            })}
          </div>
          {hasMore ? (
            <button
              type="button"
              className="btn-secondary text-xs mt-4 w-full justify-center"
              disabled={loading}
              onClick={() => load({ page: page + 1, q })}
            >
              Load more
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
